'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FloatingMobileCTAProps {
  onPurchaseClick: () => void;
  price?: string;
  label?: string;
  showCartSummary?: boolean;
  cartValue?: string;
}

const FloatingMobileCTA = ({
  onPurchaseClick,
  price = '$59.99',
  label = 'Secure Your Drop',
  showCartSummary = false,
  cartValue = '$59.99',
}: FloatingMobileCTAProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Show after scrolling past hero section (200px instead of 100px)
      if (currentScrollY < 200) {
        setIsVisible(false);
        return;
      }

      // Only hide very close to bottom (within 50px instead of 100px)
      if (currentScrollY + windowHeight >= documentHeight - 50) {
        setIsVisible(false);
        return;
      }

      // Check for inline CTAs with more lenient visibility
      const inlineCTAs = document.querySelectorAll('[data-inline-cta]');
      let nearInlineCTA = false;

      inlineCTAs.forEach((cta) => {
        const rect = cta.getBoundingClientRect();
        // Only hide if inline CTA is centered on screen (more lenient range)
        if (rect.top >= windowHeight * 0.3 && rect.bottom <= windowHeight * 0.7) {
          nearInlineCTA = true;
        }
      });

      if (nearInlineCTA) {
        setIsVisible(false);
        return;
      }

      // Keep visible during scroll (removed the hide-on-scroll-down logic)
      setIsVisible(true);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleClick = () => {
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    onPurchaseClick();
  };

  return (
    <div data-component-id="src/components/common/FloatingMobileCTA.tsx:78:4" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="78" data-component-end-line="108" data-component-file="FloatingMobileCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`lg:hidden fixed bottom-0 left-0 right-0 z-[9999] transition-all duration-300 ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      {/* Backdrop blur and shadow for better visibility */}
      <div data-component-id="src/components/common/FloatingMobileCTA.tsx:84:6" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="84" data-component-end-line="107" data-component-file="FloatingMobileCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-background%2F98%20backdrop-blur-md%20border-t-2%20border-primary%2F30%20shadow-%5B0_-4px_20px_rgba(255%2C176%2C0%2C0.2)%5D%22%7D" className="bg-background/98 backdrop-blur-md border-t-2 border-primary/30 shadow-[0_-4px_20px_rgba(255,176,0,0.2)]">
        <div data-component-id="src/components/common/FloatingMobileCTA.tsx:85:8" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="85" data-component-end-line="106" data-component-file="FloatingMobileCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20py-3%20max-w-screen-sm%20mx-auto%22%7D" className="px-4 py-3 max-w-screen-sm mx-auto">
          {showCartSummary ? (
            <div data-component-id="src/components/common/FloatingMobileCTA.tsx:87:12" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="87" data-component-end-line="93" data-component-file="FloatingMobileCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-2%22%7D" className="flex items-center justify-between mb-2">
              <div data-component-id="src/components/common/FloatingMobileCTA.tsx:88:14" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="88" data-component-end-line="91" data-component-file="FloatingMobileCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
                <Icon data-component-id="src/components/common/FloatingMobileCTA.tsx:89:16" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="89" data-component-end-line="89" data-component-file="FloatingMobileCTA.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ShoppingCartIcon%22%2C%22className%22%3A%22text-text-secondary%22%7D" name="ShoppingCartIcon" size={18} className="text-text-secondary" />
                <span data-component-id="src/components/common/FloatingMobileCTA.tsx:90:16" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="90" data-component-end-line="90" data-component-file="FloatingMobileCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-text-secondary%22%2C%22textContent%22%3A%22Cart%20Total%3A%22%7D" className="text-xs text-text-secondary">Cart Total:</span>
              </div>
              <span data-component-id="src/components/common/FloatingMobileCTA.tsx:92:14" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="92" data-component-end-line="92" data-component-file="FloatingMobileCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-base%20font-bold%20text-foreground%22%7D" className="text-base font-bold text-foreground">{cartValue}</span>
            </div>
          ) : null}

          {/* Tokyo convenience store themed button */}
          <button data-component-id="src/components/common/FloatingMobileCTA.tsx:97:10" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="97" data-component-end-line="105" data-component-file="FloatingMobileCTA.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-center%20space-x-2%20px-4%20py-3.5%20bg-primary%20text-primary-foreground%20rounded-lg%20font-bold%20text-base%20shadow-cta%20transition-smooth%20hover%3Ascale-%5B1.02%5D%20active%3Ascale-95%20border-2%20border-primary%2F20%20animate-cta-pulse-glow%22%7D"
            onClick={handleClick}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3.5 bg-primary text-primary-foreground rounded-lg font-bold text-base shadow-cta transition-smooth hover:scale-[1.02] active:scale-95 border-2 border-primary/20 animate-cta-pulse-glow"
            style={{ minHeight: '48px' }}
          >
            <span data-component-id="src/components/common/FloatingMobileCTA.tsx:102:12" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="102" data-component-end-line="102" data-component-file="FloatingMobileCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22truncate%22%7D" className="truncate">{label}</span>
            <span data-component-id="src/components/common/FloatingMobileCTA.tsx:103:12" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="103" data-component-end-line="103" data-component-file="FloatingMobileCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-extrabold%20whitespace-nowrap%22%7D" className="font-extrabold whitespace-nowrap">{price}</span>
            <Icon data-component-id="src/components/common/FloatingMobileCTA.tsx:104:12" data-component-path="src/components/common/FloatingMobileCTA.tsx" data-component-line="104" data-component-end-line="104" data-component-file="FloatingMobileCTA.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%2C%22className%22%3A%22flex-shrink-0%22%7D" name="ArrowRightIcon" size={18} className="flex-shrink-0" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FloatingMobileCTA;